/**
 * The pure half of `TurnCards.tsx`: which of an answer's cards the chat can
 * draw, in which variant, and the few lines the compact variants print.
 *
 * **What arrives is the graph's own snapshot** (`TurnCard`, from
 * `additional_kwargs.pa_turn_cards` or a `turn_cards` frame): the board node as
 * it stood when the turn ended. It goes through the SAME `validateProps` the
 * board uses — a snapshot is not a second contract — and anything the catalogue
 * does not know, or knows as «drawn elsewhere» (chips, the board's structure),
 * is skipped in silence: one unreadable card may not cost the answer the rest.
 *
 * **Five components get a compact variant**, because on the board they are the
 * WIDE ones (`registry.ts`'s `wide`, plus `PriceTable`'s table and `MapCard`,
 * which may only exist once per page as a live 2GIS map — `map/mapHost.ts`):
 * the full version stays on the board, and the chat column (`--chat-width`)
 * gets a preview and a way to it. Everything else is narrow already and is the
 * catalogue's own component.
 *
 * **The variant is decided by the GRAPH, not by the card** (`hasNoBoard`,
 * §plan «Новый агент», 18.09; #132): `peterhof_orch` has a board to point the
 * previews at, `peterhof_agent` has none, and the chat is the only place its
 * cards are ever shown. So on a board-less graph NO variant carries a «на
 * доске» button: `TripPlan` renders in FULL (`'plan-full'`), `MapCard` as the
 * sketch with its totals (`'map-sketch'` — the one live map per page belongs to
 * a board), and every other card as the catalogue's own component, whole —
 * `PriceTable` folds its rows in place with its own `CollapsibleRows`
 * («показано X из Y»), `ObjectStrip` and `NoteCard` show everything.
 *
 * **Only the newest snapshot of a card id is editable** there (#132): the same
 * `plan:main` is drawn under every answer that changed it, and a `✕` or an
 * action on yesterday's copy would edit today's plan. `latestSnapshots` says
 * which answer holds the newest one; `catalog/inlineCard.ts` applies it.
 *
 * Nothing here recomputes the graph's facts: the numbers, the order, the
 * totals and the prices are printed as they came, only fewer of them.
 */

import { catalogEntry, validateProps } from '../catalog/registry';
import { planTotals } from '../export/planExport';
import type {
  MapCardProps,
  ObjectStripProps,
  PriceTableProps,
  TripPlanProps,
} from '../catalog/generated/schemas';
import type { Bubble } from '../store/chatStore';
import { hasNoBoard, showsTurnCards } from '../store/sessionStore';
import type { TurnCard } from '../transport/events';

export type InlineVariant = 'map' | 'map-sketch' | 'plan' | 'plan-full' | 'strip' | 'prices' | 'note' | 'card';

export type InlineCard = {
  id: string;
  component: string;
  variant: InlineVariant;
  /** The validated node, as the catalogue component reads it. */
  props: Record<string, unknown>;
  /**
   * `TripPlan.change_note` — a graph's own note on what a `plan_edit` call
   * just changed («+Марли, −Эрмитаж»), read off the validated props (the
   * catalogue declares the field).
   */
  changeNote?: string;
};

const COMPACT: Record<string, InlineVariant> = {
  MapCard: 'map',
  TripPlan: 'plan',
  ObjectStrip: 'strip',
  PriceTable: 'prices',
  NoteCard: 'note',
};

/** A board-less graph: nothing points at a board; anything absent here is `'card'`. */
const NO_BOARD: Record<string, InlineVariant> = {
  MapCard: 'map-sketch',
  TripPlan: 'plan-full',
};

/** A placeholder is never an answer's card; the graph does not send one either. */
const NEVER_INLINE = new Set(['SkeletonCard']);

export const PLAN_PREVIEW_STOPS = 4;
export const PRICE_PREVIEW_ROWS = 3;
export const STRIP_PREVIEW_TILES = 5;
export const NOTE_PREVIEW_CHARS = 280;

/**
 * The cards under ONE bubble, or none. `sessionStore.showsTurnCards` is asked
 * here rather than in `Thread.tsx` so the rule «only `peterhof_orch`, only a
 * guide's answer» has one reader and a test.
 */
export function cardsUnder(bubble: Pick<Bubble, 'role' | 'cards'>, assistantId: string): TurnCard[] {
  if (!showsTurnCards(assistantId) || bubble.role !== 'guide') return [];
  return bubble.cards ?? [];
}

/**
 * Card id → the id of the answer holding its NEWEST snapshot, over the whole
 * thread in order. Empty on a graph that draws no cards under its answers.
 * Counted over the cards that are DRAWN (`inlineCards`): a newest snapshot the
 * catalogue refused is not on screen, so it may not make the one before it
 * read-only. The erase answer takes `plan:main` over with a note
 * (`tools/forget.py`), which is what makes an erased plan read-only.
 */
export function latestSnapshots(
  bubbles: readonly Pick<Bubble, 'id' | 'role' | 'cards'>[],
  assistantId: string,
): Map<string, string> {
  const latest = new Map<string, string>();
  for (const bubble of bubbles) {
    const cards = cardsUnder(bubble, assistantId);
    if (cards.length === 0) continue;
    for (const card of inlineCards(cards, assistantId)) latest.set(card.id, bubble.id);
  }
  return latest;
}

function readChangeNote(props: Record<string, unknown>): string | undefined {
  const value = props.change_note;
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

export function inlineCards(cards: readonly TurnCard[] | undefined, assistantId: string): InlineCard[] {
  const noBoard = hasNoBoard(assistantId);
  const drawn: InlineCard[] = [];
  for (const card of cards ?? []) {
    const entry = catalogEntry(card.component);
    if (!entry?.Component || NEVER_INLINE.has(card.component)) continue;
    const checked = validateProps(card.component, { ...card.props, id: card.id, component: card.component });
    if (!checked.ok) {
      // eslint-disable-next-line no-console
      console.warn('[turn-cards] skipped', card.id, checked.error);
      continue;
    }
    // A board-less graph's compact preview would point its «на доске» button
    // at a panel that does not exist — it gets the whole card instead.
    const variant: InlineVariant = noBoard
      ? (NO_BOARD[card.component] ?? 'card')
      : (COMPACT[card.component] ?? 'card');
    const props = checked.value as Record<string, unknown>;
    drawn.push({
      id: card.id,
      component: card.component,
      variant,
      props,
      ...(variant === 'plan-full' ? { changeNote: readChangeNote(props) } : {}),
    });
  }
  return drawn;
}

/** Russian needs three forms, and «8 точка» is what a visitor notices first. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = n % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

export function formatDistance(metres: number): string {
  if (metres < 1000) return `${Math.round(metres)} м`;
  return `${(metres / 1000).toFixed(1).replace('.', ',')} км`;
}

export function formatDuration(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} мин`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)} ч ${rest} мин` : `${minutes / 60} ч`;
}

/**
 * «3 точки · 2,1 км · ~25 мин» under a route sketch. The distance and the time
 * are the route's own totals (2GIS's, when they are there) and are left out
 * when absent rather than estimated from the pins.
 */
export function routeSummary(props: Pick<MapCardProps, 'pins' | 'total_distance_m' | 'total_duration_s'>): string {
  const stops = props.pins.filter((pin) => pin.kind === 'sight').length || props.pins.length;
  const parts = [`${stops} ${plural(stops, 'точка', 'точки', 'точек')}`];
  if (typeof props.total_distance_m === 'number') parts.push(formatDistance(props.total_distance_m));
  if (typeof props.total_duration_s === 'number') parts.push(`~${formatDuration(props.total_duration_s)}`);
  return parts.join(' · ');
}

type PlanItem = NonNullable<TripPlanProps['days'][number]['items']>[number];

/** The first numbered stops of a plan, in its own order, and how many are left. */
export function planPreview(
  props: TripPlanProps,
  limit = PLAN_PREVIEW_STOPS,
): { stops: PlanItem[]; more: number; totals: string } {
  const all = props.days.flatMap((day) => day.items ?? []).filter((item) => item.kind === 'stop');
  return { stops: all.slice(0, limit), more: Math.max(0, all.length - limit), totals: planTotals(props) };
}

type PriceRow = PriceTableProps['rows'][number];

/**
 * The rows the code already answered from (`highlight`) when there are any —
 * that is what the answer above is about — otherwise the table's first rows.
 */
export function pricePreview(props: PriceTableProps, limit = PRICE_PREVIEW_ROWS): { rows: PriceRow[]; more: number } {
  const picked = new Set(props.highlight ?? []);
  const highlighted = picked.size > 0 ? props.rows.filter((row) => row.key && picked.has(row.key)) : [];
  const rows = (highlighted.length > 0 ? highlighted : props.rows).slice(0, limit);
  return { rows, more: props.rows.length - rows.length };
}

type Tile = ObjectStripProps['tiles'][number];

export function stripPreview(props: ObjectStripProps, limit = STRIP_PREVIEW_TILES): { tiles: Tile[]; more: number } {
  return { tiles: props.tiles.slice(0, limit), more: Math.max(0, props.tiles.length - limit) };
}

/** A whole-word cut with an ellipsis, or the text itself when it fits. */
export function clip(text: string, max = NOTE_PREVIEW_CHARS): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}
